import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Steps, Button, Space, Spin, message, Descriptions, Tag, Alert
} from 'antd';
import {
  ArrowLeftOutlined, UserOutlined, RocketOutlined,
  HomeOutlined, EyeOutlined, SendOutlined, FileTextOutlined, TeamOutlined,
  EnvironmentOutlined, PaperClipOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import AuthService from '../../utils/auth';
import StepTouristCheck from './StepTouristCheck';
import StepFlightInfo from './StepFlightInfo';
import StepHotelAssignment from './StepHotelAssignment';
import StepTicketUpload from './StepTicketUpload';
import StepPreview from './StepPreview';
import StepPublishSend from './StepPublishSend';
import StepGuideAssignment from './StepGuideAssignment';
import StepAucklandMeeting from './StepAucklandMeeting';
import StepAttachmentUpload from './StepAttachmentUpload';
import StepNoticeTemplate from './StepNoticeTemplate';
import './NoticeWorkbench.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

// 全量步骤定义，每个步骤带一个唯一 key 便于后续过滤
const ALL_STEPS = [
  { key: 'touristCheck',      title: '游客确认',     icon: <UserOutlined /> },
  { key: 'aucklandMeeting',   title: '领队信息',     icon: <EnvironmentOutlined /> },
  { key: 'flightInfo',        title: '航班信息',     icon: <RocketOutlined /> },
  { key: 'hotelAssignment',   title: '酒店分配',     icon: <HomeOutlined /> },
  { key: 'ticketUpload',      title: '机票上传',     icon: <FileTextOutlined /> },
  { key: 'attachmentUpload',  title: '附件上传',     icon: <PaperClipOutlined /> },
  { key: 'noticeTemplate',    title: '通知书模板',   icon: <FileTextOutlined /> },
  { key: 'guideAssignment',   title: '导游分配',     icon: <TeamOutlined /> },
  { key: 'preview',           title: '预览检查',     icon: <EyeOutlined /> },
  { key: 'publishSend',       title: '通知书发送',   icon: <SendOutlined /> },
];

// 一日游隐藏：机票、航班、酒店
const DAYTOUR_HIDDEN_STEPS = new Set(['aucklandMeeting', 'ticketUpload', 'attachmentUpload', 'flightInfo', 'hotelAssignment']);
// 出境游隐藏：导游分配
const STANDARD_HIDDEN_STEPS = new Set(['guideAssignment']);

const NoticeWorkbench = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [tour, setTour] = useState(null);
  const [tourists, setTourists] = useState([]);
  const [tourNotice, setTourNotice] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTour = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/tours/${tourId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        setTour(res.data.data);
      }
    } catch (err) {
      console.error('获取团组信息失败:', err);
    }
  }, [tourId]);

  const fetchTourists = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/tourists/tour/${tourId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        setTourists(res.data.data);
      }
    } catch (err) {
      console.error('获取游客列表失败:', err);
    }
  }, [tourId]);

  const fetchNotice = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/tour-notices/tour/${tourId}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        setTourNotice(res.data.data);
      }
    } catch (err) {
      console.error('获取通知书失败:', err);
    }
  }, [tourId]);

  const fetchCompleteness = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/tour-notices/tour/${tourId}/completeness`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success) {
        setCompleteness(res.data.data);
      }
    } catch (err) {
      console.error('获取完整性数据失败:', err);
    }
  }, [tourId]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchTour(), fetchTourists(), fetchNotice(), fetchCompleteness()]);
    } finally {
      setLoading(false);
    }
  }, [fetchTour, fetchTourists, fetchNotice, fetchCompleteness]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const refreshTourists = async () => {
    await Promise.all([fetchTourists(), fetchCompleteness()]);
  };

  const refreshCompleteness = async () => {
    await fetchCompleteness();
  };

  const refreshNotice = async () => {
    await Promise.all([fetchNotice(), fetchCompleteness()]);
  };

  const refreshAll = async () => {
    await Promise.all([fetchTour(), fetchTourists(), fetchNotice(), fetchCompleteness()]);
  };

  // 根据团型动态生成可见步骤列表
  const isDaytour = tour?.tourType === 'daytour';
  const visibleSteps = ALL_STEPS.filter((step) => {
    if (isDaytour && DAYTOUR_HIDDEN_STEPS.has(step.key)) return false;
    if (!isDaytour && STANDARD_HIDDEN_STEPS.has(step.key)) return false;
    return true;
  });

  // 计算单个步骤（按 key）的完成状态
  const getStepStatusByKey = (key) => {
    if (!completeness) return 'wait';
    const stats = completeness.tour?.touristStats;
    const tourLevel = completeness.tour;

    switch (key) {
      case 'touristCheck': {
        if (!stats || stats.total === 0) return 'wait';
        const readyField = isDaytour ? 'documentReady' : 'passportReady';
        const ready = stats[readyField] ?? stats.passportReady ?? 0;
        if (ready === stats.total && stats.hasSalespersonEmail === stats.total) return 'finish';
        if (ready > 0) return 'process';
        return 'wait';
      }
      case 'aucklandMeeting': {
        // 全部为基础款（不含领队服务）时无需领队，视为完成
        const allBasic = tourists.length > 0 && tourists.every(t => t.packageType === 'no_tour');
        if (allBasic) return 'finish';
        if (tourLevel?.hasGuide) return 'finish';
        return 'wait';
      }
      case 'ticketUpload':
        if (!stats || stats.total === 0) return 'wait';
        if (stats.flightTicket === stats.total) return 'finish';
        if (stats.flightTicket > 0) return 'process';
        return 'wait';
      case 'attachmentUpload':
        if (!stats || stats.total === 0) return 'wait';
        if (stats.attachmentUploaded === stats.total) return 'finish';
        if (stats.attachmentUploaded > 0) return 'process';
        return 'wait';
      case 'flightInfo':
        if (tourLevel?.hasFlightInfo) return 'finish';
        return 'wait';
      case 'hotelAssignment':
        if (!stats || stats.total === 0) return 'wait';
        if (stats.hotelAssigned === stats.total) return 'finish';
        if (stats.hotelAssigned > 0) return 'process';
        return 'wait';
      case 'guideAssignment':
        if (tourLevel?.hasGuide) return 'finish';
        return 'wait';
      case 'preview':
        if (isDaytour) {
          if (tourLevel?.hasNotice) return 'finish';
        } else {
          if (tourLevel?.hasNotice && tourLevel?.hasFlightInfo) return 'finish';
        }
        return 'wait';
      case 'publishSend':
        if (stats?.emailSent === stats?.total && stats?.total > 0) return 'finish';
        if (stats?.emailSent > 0) return 'process';
        return 'wait';
      default:
        return 'wait';
    }
  };

  const stepsWithStatus = visibleSteps.map((item, index) => ({
    ...item,
    status: index === currentStep ? 'process' : getStepStatusByKey(item.key),
  }));

  // 根据当前可见步骤的 key 渲染内容
  const renderStepContent = () => {
    const currentKey = visibleSteps[currentStep]?.key;
    switch (currentKey) {
      case 'touristCheck':
        return <StepTouristCheck tourists={tourists} completeness={completeness} tour={tour} onRefresh={refreshTourists} />;
      case 'aucklandMeeting':
        return <StepAucklandMeeting tour={tour} tourNotice={tourNotice} tourists={tourists} onRefresh={refreshAll} />;
      case 'ticketUpload':
        return <StepTicketUpload tourists={tourists} tour={tour} completeness={completeness} onRefresh={refreshTourists} />;
      case 'attachmentUpload':
        return <StepAttachmentUpload tourists={tourists} tour={tour} tourNotice={tourNotice} completeness={completeness} onRefresh={refreshTourists} />;
      case 'noticeTemplate':
        return <StepNoticeTemplate tour={tour} onRefresh={refreshAll} />;
      case 'flightInfo':
        return <StepFlightInfo tour={tour} tourNotice={tourNotice} onRefresh={refreshNotice} />;
      case 'hotelAssignment':
        return <StepHotelAssignment tourists={tourists} tour={tour} completeness={completeness} onRefresh={refreshTourists} />;
      case 'guideAssignment':
        return <StepGuideAssignment tour={tour} completeness={completeness} onRefresh={refreshAll} />;
      case 'preview':
        return <StepPreview tourists={tourists} tour={tour} tourNotice={tourNotice} completeness={completeness} onRefresh={refreshCompleteness} />;
      case 'publishSend':
        return <StepPublishSend tourists={tourists} tour={tour} tourNotice={tourNotice} completeness={completeness} onRefresh={refreshAll} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!tour) {
    return <Alert type="error" message="未找到该团组信息" showIcon />;
  }

  return (
    <div className="notice-workbench">
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tours')}>返回</Button>
            <span>通知书工作台</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
          <Descriptions.Item label="产品名称">{tour.productName}</Descriptions.Item>
          <Descriptions.Item label="出发日期">{tour.departureDate ? dayjs(tour.departureDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
          <Descriptions.Item label="目的地">
            {tour.destination?.name || <Tag color="warning">未设置</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="游客数">
            <Tag color="blue">{tourists.length} 人</Tag>
          </Descriptions.Item>
          {isDaytour && (
            <Descriptions.Item label="团型">
              <Tag color="cyan">一日游</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <Steps
          current={currentStep}
          onChange={(step) => setCurrentStep(step)}
          items={stepsWithStatus}
          style={{ marginBottom: 16 }}
        />

        <div className="step-content">
          {renderStepContent()}
        </div>

        <div className="step-actions">
          <Button
            disabled={currentStep === 0}
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            上一步
          </Button>
          <Button
            type="primary"
            disabled={currentStep === visibleSteps.length - 1}
            onClick={() => setCurrentStep(currentStep + 1)}
          >
            下一步
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default NoticeWorkbench;
